import React, { useState, ChangeEvent, useEffect, useRef, useCallback } from 'react';
import ShareListingModal from 'components/ShareListingModal';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './Ilan_detay_login.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import Button from 'components/Button';
import { listingService } from 'api/services/ListingService';
import { favoriteListingService } from 'api/services/FavoriteListingService';
import { conversationService } from 'api/services/ConversationService';
import { userService } from 'api/services/UserService';
import { useAuth, hasAdminPanelAccess } from 'contexts/AuthContext';
import ReportModal from 'components/moderation/ReportModal';
import { ReportContentType } from 'api/types/moderation';
import {
  type ListingDetailProduct,
  listingViewModelToDetailProduct,
} from 'api/utils/listingDetailMapper';
import ListingSpecsSection from 'components/Listing/ListingSpecsSection';
import LocationMapEmbed from 'components/Listing/LocationMapEmbed';
import ListingReelsSection from 'components/reels/ListingReelsSection';
import { usePerformanceTest } from '../hooks/usePerformanceTest';
import { ApiResultBlock } from '../components/ListingPerformance/ApiResultBlock';
import Navbar from 'components/Header/Navbar';
import Footer from 'components/Footer';
import BuyerVerifyButton from 'components/Listing/BuyerVerifyButton';
import RatingModal from 'components/Rating/RatingModal';

const Ilan_detay_login: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [product, setProduct] = useState<ListingDetailProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isUserFavorite, setIsUserFavorite] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);
  const [selectedVideo, setSelectedVideo] = useState<string>('');
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [selectedApp, setSelectedApp] = useState<string>('');
  const [showPhone, setShowPhone] = useState(false);
  const videoUrl: string = "https://www.youtube.com/embed/your-video-id";

  const [contactSellerLoading, setContactSellerLoading] = useState(false);
  const [showMarkAsSoldModal, setShowMarkAsSoldModal] = useState(false);
  const [markAsSoldBuyers, setMarkAsSoldBuyers] = useState<{ id: string; name: string }[]>([]);
  const [markAsSoldLoadingBuyers, setMarkAsSoldLoadingBuyers] = useState(false);
  const [markAsSoldSubmitting, setMarkAsSoldSubmitting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const shareButtonRef = useRef<HTMLButtonElement | null>(null);
  const [markAsSoldBuyerId, setMarkAsSoldBuyerId] = useState('');
  const [markAsSoldDate, setMarkAsSoldDate] = useState(() => new Date().toISOString().slice(0, 16));
  const [ratingModalOpen, setRatingModalOpen] = useState(false);

  const performanceTest = usePerformanceTest({
    specs: product?.specs ?? {},
  });

  const fetchListing = useCallback(async (listingId: string) => {
    setLoading(true);
    setFetchError(null);
    try {
      const result = await listingService.getById(listingId);
      if (result.isSuccess && result.data) {
        setProduct(listingViewModelToDetailProduct(result.data));
        setIsUserFavorite(result.data.isUserFavorite ?? false);
        listingService.incrementView(listingId).catch(() => { });
      } else {
        setProduct(null);
        setFetchError(result.message ?? 'İlan bulunamadı.');
      }
    } catch (err) {
      setProduct(null);
      setFetchError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchListing(id);
    } else {
      setLoading(false);
      setFetchError('İlan kodu belirtilmedi.');
    }
  }, [id, fetchListing]);

  // Initialize bootstrap carousel
  useEffect(() => {
    if (carouselRef.current && typeof window !== 'undefined' && window.bootstrap) {
      try {
        // Initialize the carousel with bootstrap
        new window.bootstrap.Carousel(carouselRef.current, {
          interval: 5000 // set auto-cycling interval in ms, or false to disable auto-cycling
        });
        console.log("Carousel initialized successfully");
      } catch (error) {
        console.error("Error initializing carousel:", error);
      }
    } else {
      console.warn("Could not initialize carousel - bootstrap or carousel element not available");
    }
  }, []);

  const logout = (): void => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userEmail');
    window.location.href = '/';
  };

  const handleLogout = (): void => {
    localStorage.removeItem('isLoggedIn');
    navigate('/giris');
  };

  const handleShowVideo = (): void => {
    setShowVideo(true);
  };

  const handleSlideChange = (index: number): void => {
    setActiveIndex(index);
  };

  const handleStartChat = (): void => {
    if (!localStorage.getItem('isLoggedIn')) {
      alert('Mesaj göndermek için giriş yapmalısınız.');
      navigate('/giris');
      return;
    }
    // Non-owner: contact seller flow is handled by handleContactSeller
    navigate('/mesajlar');
  };

  const handleContactSeller = async (): Promise<void> => {
    if (!id || !user?.id) return;
    setContactSellerLoading(true);
    try {
      const result = await conversationService.contactSeller(id);
      if (result.isSuccess && result.data?.id) {
        navigate(`/mesajlar?conversationId=${result.data.id}`);
      } else {
        alert(result.message ?? 'İletişim başlatılamadı.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setContactSellerLoading(false);
    }
  };

  const loadConversationParticipantsForBuyer = useCallback(async (currentUserId: string) => {
    setMarkAsSoldLoadingBuyers(true);
    try {
      const res = await conversationService.getConversations({
        'Pagination.PageSize': 100,
        'Pagination.PageNumber': 1,
      });
      const list = res.data ?? [];
      const peerIds = new Set<string>();
      for (const c of list) {
        const other = c.senderId === currentUserId ? c.receiverId : c.senderId;
        if (other) peerIds.add(other);
      }
      const names: { id: string; name: string }[] = [];
      for (const uid of Array.from(peerIds)) {
        const u = await userService.getById(uid);
        const uu = u.data;
        const name = uu ? [uu.firstName, uu.lastName].filter(Boolean).join(' ') || uu.email || uid : uid;
        names.push({ id: uid, name });
      }
      names.sort((a, b) => a.name.localeCompare(b.name));
      setMarkAsSoldBuyers(names);
    } catch {
      setMarkAsSoldBuyers([]);
    } finally {
      setMarkAsSoldLoadingBuyers(false);
    }
  }, []);

  const handleOpenMarkAsSoldModal = (): void => {
    setMarkAsSoldBuyerId('');
    setMarkAsSoldDate(new Date().toISOString().slice(0, 16));
    if (user?.id) loadConversationParticipantsForBuyer(user.id);
    setShowMarkAsSoldModal(true);
  };

  const handleCloseMarkAsSoldModal = (): void => {
    setShowMarkAsSoldModal(false);
  };

  const handleMarkAsSoldSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!id || !markAsSoldBuyerId || !markAsSoldDate || markAsSoldSubmitting) return;
    setMarkAsSoldSubmitting(true);
    try {
      const soldDateIso = new Date(markAsSoldDate).toISOString();
      const result = await listingService.markAsSold(id, {
        soldToUserId: markAsSoldBuyerId,
        soldDate: soldDateIso,
      });
      if (result.isSuccess) {
        alert('İlan satıldı olarak işaretlendi.');
        handleCloseMarkAsSoldModal();
        fetchListing(id);
      } else {
        alert(result.message ?? 'İşlem başarısız.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setMarkAsSoldSubmitting(false);
    }
  };

  const handleShowPhone = (): void => {
    if (!isAuthenticated) {
      alert('Telefon numarasını görmek için giriş yapmalısınız.');
      navigate('/giris');
      return;
    }
    setShowPhone(true);
  };

  const handleShareListing = (): void => {
    setShowShareModal(true);
  };

  const handleThumbnailClick = (index: number): void => {
    setActiveIndex(index);
  };

  const handleToggleFavorite = async (): Promise<void> => {
    if (!user?.id || !product) return;
    const result = await favoriteListingService.toggle({
      userId: user.id,
      listingId: product.id,
    });
    if (result.isSuccess) setIsUserFavorite((prev) => !prev);
    else if (result.message) window.alert(result.message);
  };

  const handleVideoClick = (url: string): void => {
    setSelectedVideo(url);
    setShowVideoModal(true);
  };

  const handleGameSelect = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setSelectedGame(e.target.value);
    // Show FPS display when a game is selected
    const fpsDisplay = document.getElementById('fpsDisplay');
    if (fpsDisplay) {
      if (e.target.value) {
        fpsDisplay.classList.remove('d-none');
      } else {
        fpsDisplay.classList.add('d-none');
      }
    }
  };

  const handleAppSelect = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setSelectedApp(e.target.value);
    // Show app compatibility when an app is selected
    const appDisplay = document.getElementById('appDisplay');
    if (appDisplay) {
      if (e.target.value) {
        appDisplay.classList.remove('d-none');
      } else {
        appDisplay.classList.add('d-none');
      }
    }
  };

  const handleTestGame = (): void => {
    if (!selectedGame) {
      alert('Lütfen önce bir oyun seçiniz.');
      return;
    }
    performanceTest.fps.run(selectedGame);
  };

  const handleTestApp = (): void => {
    if (!selectedApp) {
      alert('Lütfen önce bir uygulama seçiniz.');
      return;
    }
    performanceTest.compatibility.run(selectedApp);
  };

  if (loading) {
    return (
      <div className="min-vh-100 sb-page-bg">
        <div className="container py-5 text-center text-muted">
          <div className="spinner-border text-primary mb-2" role="status" />
          <p>İlan yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (fetchError || !product) {
    return (
      <div className="min-vh-100 sb-page-bg">
        <div className="container py-5 text-center">
          <p className="text-danger mb-3">{fetchError ?? 'İlan bulunamadı.'}</p>
          <Link className="btn btn-primary" to="/">Ana Sayfaya Dön</Link>
        </div>
      </div>
    );
  }

  const isListingOwner = !!(user?.id && product.sellerId && user.id === product.sellerId);

  const isListingSold =
    product.statusId === 3 ||
    (product.statusName?.toLowerCase() === 'sold');

  return (
    <div className="min-vh-100 sb-page-bg">
      <Navbar />
      <main className="container mt-4">
        <div className="row">
          {/* Sol Taraf - Resim Galerisi ve Açıklama */}
          <div className="col-md-8 mb-4">
            {/* Ana Carousel */}
            <div className="carousel slide mb-3" data-bs-ride="false" id="productCarousel" ref={carouselRef}>
              <div className="carousel-inner rounded">
                <div className="carousel-item active">
                  {showVideo ? (
                    <iframe
                      width="100%"
                      height="400"
                      src={videoUrl}
                      title="Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <img
                      src={product.images[0]}
                      className="d-block w-100"
                      alt={product.title}
                    />
                  )}
                </div>
                {product.images.slice(1).map((image, idx) => (
                  <div key={idx} className="carousel-item">
                    <img alt={`${product.title} ${idx + 2}`} className="d-block w-100" src={image} />
                  </div>
                ))}
              </div>
              {/* Kontrol butonları */}
              <Button className="carousel-control-prev custom-carousel-control" data-bs-slide="prev" data-bs-target="#productCarousel" type="button">
                <span className="carousel-control-icon rounded-circle">
                  <i className="fas fa-chevron-left"></i>
                </span>
              </Button>
              <Button className="carousel-control-next custom-carousel-control" data-bs-slide="next" data-bs-target="#productCarousel" type="button">
                <span className="carousel-control-icon rounded-circle">
                  <i className="fas fa-chevron-right"></i>
                </span>
              </Button>
            </div>
            {/* Thumbnail Gallery */}
            <div className="thumbnail-gallery-container mb-4">
              <div className="thumbnail-gallery d-flex gap-2 overflow-auto px-2 py-1">
                {/* Video Thumbnail */}
                <div className="thumbnail video-thumb" onClick={() => handleVideoClick(videoUrl)}>
                  <img alt="Video Önizleme" className="rounded" src={product.images[0]} />
                  <div className="thumbnail-overlay"></div>
                  <div className="video-icon">
                    <i className="fas fa-play"></i>
                  </div>
                </div>
                {product.images.map((image, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${activeIndex === index ? 'active' : ''}`}
                    onClick={() => handleThumbnailClick(index)}
                  >
                    <img alt={`Thumbnail ${index + 1}`} className="rounded" src={image} />
                    <div className="thumbnail-overlay"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Feature 009 (US3): reels associated with this listing. */}
            <ListingReelsSection listingId={product.id} />

            <ListingSpecsSection specs={product.specs} />
            {/* Açıklama Kartı */}
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Açıklama</h5>
                <p>{product.description}</p>
              </div>
            </div>

            {/* İlan Konumu Kartı */}
            <div className="card mt-4">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                  İlan Konumu
                </h5>
                <p className="mb-3">
                  <strong>Konum:</strong> {[product.location.district, product.location.city].filter(Boolean).join(', ') || 'Belirtilmemiş'}
                  {product.location.address ? ` – ${product.location.address}` : ''}
                </p>
                {(product.location.district || product.location.city) && (
                  <LocationMapEmbed
                    query={[product.location.address, product.location.district, product.location.city]
                      .filter(Boolean)
                      .join(', ')}
                  />
                )}
              </div>
            </div>
          </div>
          {/* Sağ Taraf - İlan Bilgileri */}
          <div className="col-md-4">
            {/* Satıcı Bilgileri */}
            <div className="card seller-info mb-4">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <img alt="Satıcı" className="rounded-circle me-3" height="50" src={product.seller.avatar} width="50" />
                  <div>
                    <Link
                      className="text-decoration-none"
                      to={
                        product.sellerId
                          ? product.seller.accountType === 'Corporate'
                            ? `/satici-profil-kurumsal/${product.sellerId}`
                            : `/satici-profil/${product.sellerId}`
                          : '#'
                      }
                    >
                      <h5 className="mb-0">{product.seller.name}</h5>
                    </Link>
                    <small className="text-muted">Üye: {product.seller.joinDate}</small>
                  </div>
                </div>
                <div className="d-grid gap-2">
                  {user?.id === product.sellerId ? (
                    <>
                      <Link className="btn btn-outline-primary" to={`/ilan-duzenle/${product.id}`}>
                        <i className="fas fa-edit me-2"></i>
                        İlanı Düzenle
                      </Link>
                      <Button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleOpenMarkAsSoldModal}
                        disabled={isListingSold}
                        title={isListingSold ? 'Bu ilan zaten satıldı olarak işaretli' : undefined}
                      >
                        <i className="fas fa-tag me-2"></i>
                        Satıldı Olarak İşaretle
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        className="btn btn-primary"
                        onClick={handleContactSeller}
                        disabled={contactSellerLoading || isListingSold}
                        title={isListingSold ? 'Bu ilan satıldı' : undefined}
                      >
                        {contactSellerLoading ? (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        ) : (
                          <i className="fas fa-comment me-2"></i>
                        )}
                        Satıcıyla İletişime Geç
                      </Button>
                      {product.showPhoneOnListing ? (
                      <Button className="btn btn-outline-primary" onClick={handleShowPhone}
                        disabled={showPhone && Boolean(product.sellerPhoneNumber)}>
                        <i className="fas fa-phone me-2"></i>
                        {showPhone
                          ? product.sellerPhoneNumber || 'Telefon numarası kayıtlı değil'
                          : 'Telefonu Göster'}
                      </Button>
                      ) : null}
                      <BuyerVerifyButton
                        listingId={product.id}
                        currentUserId={user?.id}
                        soldListingSummary={product.soldListingSummary ?? null}
                        onVerified={(updated) =>
                          setProduct((prev) => (prev ? { ...prev, soldListingSummary: updated } : prev))
                        }
                      />
                      {product.soldListingSummary
                        && user?.id === product.soldListingSummary.soldToUserId
                        && product.soldListingSummary.completedAt != null
                        && !product.soldListingSummary.hasRating && (
                        <Button
                          className="btn btn-warning"
                          onClick={() => setRatingModalOpen(true)}
                        >
                          <i className="fas fa-star me-2" />
                          Satıcıyı Değerlendir
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* İlan Bilgileri */}
            <div className="card listing-info mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h1 className="h3 mb-0">{product.title}</h1>
                  {!isListingOwner && (
                    <Button
                      type="button"
                      className="btn btn-outline-primary btn-sm favorite-btn"
                      style={{ backgroundColor: 'var(--sb-bg)' }}
                      onClick={handleToggleFavorite}
                    >
                      <i className={`${isUserFavorite ? 'fas' : 'far'} fa-heart ${isUserFavorite ? 'text-danger' : 'text-primary'}`} />
                    </Button>
                  )}
                </div>
                {product.statusId === 5 &&
                  user?.id === product.sellerId &&
                  product.rejectionReason && (
                    <div className="alert alert-danger mb-3" role="alert">
                      <i className="fas fa-times-circle me-2" aria-hidden />
                      <strong>İlan reddedildi.</strong> {product.rejectionReason}
                    </div>
                  )}
                {product.isTested && (
                  <div className="mb-3">
                    <span
                      className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary px-3 py-2 d-inline-flex align-items-center gap-2 fw-semibold small"
                      title="Test Edildi rozeti: ürün doğrulanmış test sürecinden geçmiştir."
                    >
                      <i className="fas fa-shield-alt" aria-hidden />
                      Test Edildi
                    </span>
                  </div>
                )}
                <div className="price-badge mb-3 fs-4 fw-bold text-primary border border-primary rounded-2 px-2 py-1 text-center" style={{ width: 'fit-content' }}>
                  {product.price.toLocaleString('tr-TR')} ₺
                </div>
                <div className="text-muted small mb-2">
                  <i className="fas fa-hashtag me-1 text-primary"></i>
                  İlan Kodu:{' '}
                  <span className="fw-bold text-body">
                    {product.listingCode ?? product.id}
                  </span>
                </div>
                <div className="listing-meta text-muted mb-3">
                  {product.productTypeName && (
                    <div>
                      <i className="fas fa-tag me-2 text-primary"></i>
                      {product.productTypeName}
                    </div>
                  )}
                  {product.conditionName && (
                    <div>
                      <i className="fas fa-box me-2 text-primary"></i>
                      {product.conditionName}
                    </div>
                  )}
                  {(product.location.district || product.location.city) && (
                    <div>
                      <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                      {[product.location.district, product.location.city].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {product.viewCount != null && (
                    <div>
                      <i className="fas fa-eye me-2 text-primary"></i>
                      {product.viewCount} görüntülenme
                    </div>
                  )}
                </div>
                <hr />
                <div className="listing-actions mt-3">
                  <div className="btn-group w-100">
                    <Button
                      className="btn btn-outline-primary"
                      onClick={handleShareListing}
                      ref={shareButtonRef}
                    >
                      <i className="fas fa-share-alt me-2"></i>
                      Paylaş
                    </Button>
                    <Button className="btn btn-outline-danger" data-bs-target="#complaintModal" data-bs-toggle="modal">
                      <i className="fas fa-exclamation-circle me-1"></i>
                      Şikayet Et
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            {/* Satıcı bilgileri kartından sonra */}
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title mb-4">
                  <i className="fas fa-chart-line me-2 text-primary"></i>
                  Performans Özellikleri
                </h5>
                {/* Oyun Seçimi */}
                <div className="mb-4">
                  <label className="form-label">
                    Oyun Seçin
                  </label>
                  <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 sb-performance-actions">
                    <select className="form-select flex-grow-1 min-w-0" id="gameSelector" value={selectedGame} onChange={handleGameSelect}>
                        <option value="">
                          Oyun seçiniz...
                        </option>
                        <option value="gta5">
                          GTA 5
                        </option>
                        <option value="rust">
                          Rust
                        </option>
                        <option value="csgo">
                          CS:GO
                        </option>
                        <option value="valorant">
                          Valorant
                        </option>
                        <option value="fortnite">
                          Fortnite
                        </option>
                        <option value="rdr2">
                          Red Dead Redemption 2
                        </option>
                        <option value="cyberpunk">
                          Cyberpunk 2077
                        </option>
                        <option value="warzone">
                          Call of Duty: Warzone
                        </option>
                      </select>
                    <div className="d-grid d-sm-block flex-shrink-0">
                      <Button
                        type="button"
                        className="btn btn-primary"
                        id="testGameBtn"
                        onClick={handleTestGame}
                        disabled={performanceTest.fps.loading}
                      >
                        {performanceTest.fps.loading ? (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        ) : null}
                        <i className="fas fa-play-circle me-2"></i>
                        Test Et
                      </Button>
                    </div>
                  </div>
                  <ApiResultBlock
                    loading={performanceTest.fps.loading}
                    result={performanceTest.fps.result}
                    error={performanceTest.fps.error}
                  />
                </div>
                {/* FPS Göstergesi */}
                <div className="d-none" id="fpsDisplay">
                  <div className="performance-stats">
                    <div className="row align-items-center">
                      <div className="col-md-4">
                        <div className="text-center mb-3">
                          <h6 className="mb-2">
                            1080p Orta Ayarlar
                          </h6>
                          <div className="fps-badge">
                            <span className="display-4 fw-bold text-primary" id="fpsBadge">
                              --
                            </span>
                            <span className="text-muted">
                              FPS
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-8">
                        <div className="fps-bar-container">
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted small">
                              0 FPS
                            </span>
                            <span className="text-muted small">
                              144+ FPS
                            </span>
                          </div>
                          <div className="progress" style={{ height: '25px' }}>
                            <div className="progress-bar" id="fpsBar" role="progressbar" style={{ width: '0%' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Performans Detayları */}
                    <div className="mt-4">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="performance-detail mb-3">
                            <i className="fas fa-microchip text-primary me-2"></i>
                            <span className="text-muted">
                              Min. FPS:
                            </span>
                            <span className="fw-bold" id="minFps">
                              --
                            </span>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="performance-detail mb-3">
                            <i className="fas fa-tachometer-alt text-primary me-2"></i>
                            <span className="text-muted">
                              Max. FPS:
                            </span>
                            <span className="fw-bold" id="maxFps">
                              --
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Oyun Seçimi'nden sonra */}
                <div className="mb-4">
                  <label className="form-label">
                    İş Uygulamaları
                  </label>
                  <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 sb-performance-actions">
                    <select className="form-select flex-grow-1 min-w-0" id="appSelector" value={selectedApp} onChange={handleAppSelect}>
                        <option value="">
                          Uygulama seçiniz...
                        </option>
                        <optgroup label="Adobe Uygulamaları">
                          <option value="photoshop">
                            Adobe Photoshop 2024
                          </option>
                          <option value="premiere">
                            Adobe Premiere Pro 2024
                          </option>
                          <option value="aftereffects">
                            Adobe After Effects 2024
                          </option>
                          <option value="illustrator">
                            Adobe Illustrator 2024
                          </option>
                        </optgroup>
                        <optgroup label="Microsoft Office">
                          <option value="office365">
                            Microsoft 365 Suite
                          </option>
                          <option value="excel">
                            Microsoft Excel (Büyük Veri)
                          </option>
                          <option value="powerbi">
                            Microsoft Power BI
                          </option>
                        </optgroup>
                        <optgroup label="3D &amp; CAD">
                          <option value="autocad">
                            AutoCAD 2024
                          </option>
                          <option value="3dsmax">
                            3ds Max 2024
                          </option>
                          <option value="revit">
                            Revit 2024
                          </option>
                        </optgroup>
                        <optgroup label="Müzik &amp; Ses">
                          <option value="cubase">
                            Cubase Pro 12
                          </option>
                          <option value="protools">
                            Pro Tools Studio
                          </option>
                          <option value="flstudio">
                            FL Studio 21
                          </option>
                        </optgroup>
                      </select>
                    <div className="d-grid d-sm-block flex-shrink-0">
                      <Button
                        type="button"
                        className="btn btn-primary"
                        id="testAppBtn"
                        onClick={handleTestApp}
                        disabled={performanceTest.compatibility.loading}
                      >
                        {performanceTest.compatibility.loading ? (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        ) : null}
                        <i className="fas fa-play-circle me-2"></i>
                        Test Et
                      </Button>
                    </div>
                  </div>
                  <ApiResultBlock
                    loading={performanceTest.compatibility.loading}
                    result={performanceTest.compatibility.result}
                    error={performanceTest.compatibility.error}
                  />
                </div>
                {/* Uygulama Uyumluluk Göstergesi */}
                <div className="d-none" id="appDisplay">
                  <div className="app-compatibility mb-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="mb-1" id="appName">
                          Adobe Photoshop 2024
                        </h6>
                        <p className="text-muted small mb-0" id="appRequirements">
                          Tavsiye Edilen Sistem Gereksinimleri
                        </p>
                      </div>
                      <div className="badge bg-success" id="compatibilityBadge">
                        Destekleniyor
                      </div>
                    </div>
                  </div>
                  <div className="app-details mt-3">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="performance-detail mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          <span id="appFeature1">
                            RAM Gereksinimi
                          </span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="performance-detail mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          <span id="appFeature2">
                            GPU Desteği
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />

      {product && (
        <ReportModal
          contentId={product.id}
          contentType={ReportContentType.Listing}
          reportedUserId={product.sellerId}
          isAuthenticated={isAuthenticated}
          isOwnContent={Boolean(user?.id && product.sellerId && user.id === product.sellerId)}
          onRequireLogin={() => {
            window.alert('Şikayet için giriş yapmalısınız.');
            navigate('/giris');
          }}
        />
      )}

      {/* Satılık İşaretle Modal - backdrop first so dialog stays on top and is clickable */}
      {showMarkAsSoldModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1} role="dialog">
          <div className="modal-backdrop fade show" onClick={handleCloseMarkAsSoldModal} aria-hidden="true" />
          <div className="modal-dialog" style={{ zIndex: 1056 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Satılık İşaretle</h5>
                <Button className="btn-close" type="button" onClick={handleCloseMarkAsSoldModal} aria-label="Kapat" />
              </div>
              <form onSubmit={handleMarkAsSoldSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="markAsSoldBuyer" className="form-label">Alıcı</label>
                    <select
                      id="markAsSoldBuyer"
                      className="form-select"
                      value={markAsSoldBuyerId}
                      onChange={(e) => setMarkAsSoldBuyerId(e.target.value)}
                      required
                      disabled={markAsSoldLoadingBuyers}
                    >
                      <option value="">Seçiniz...</option>
                      {markAsSoldBuyers.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                    {markAsSoldLoadingBuyers && (
                      <div className="form-text">
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        Konuşma listesi yükleniyor...
                      </div>
                    )}
                    {!markAsSoldLoadingBuyers && markAsSoldBuyers.length === 0 && (
                      <div className="form-text text-warning">
                        Henüz konuştuğunuz alıcı bulunmuyor. Önce satıcıyla iletişime geçen kullanıcılarla konuşmanız gerekir.
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="markAsSoldDate" className="form-label">Satış Tarihi</label>
                    <input
                      id="markAsSoldDate"
                      type="datetime-local"
                      className="form-control"
                      value={markAsSoldDate}
                      onChange={(e) => setMarkAsSoldDate(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <Button type="button" className="btn btn-secondary" onClick={handleCloseMarkAsSoldModal}>
                    İptal
                  </Button>
                  <Button
                    type="submit"
                    className="btn btn-primary"
                    disabled={markAsSoldSubmitting || markAsSoldLoadingBuyers || markAsSoldBuyers.length === 0}
                  >
                    {markAsSoldSubmitting ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    ) : null}
                    Kaydet
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideo && (
        <div className="modal fade show" id="videoModal" tabIndex={-1} style={{ display: 'block' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body p-0">
                <Button
                  className="btn-close position-absolute end-0 top-0 m-2 z-3"
                  style={{ backgroundColor: 'var(--sb-bg)' }}
                  onClick={() => setShowVideo(false)}
                ></Button>
                <video className="w-100" controls id="productVideo">
                  <source src="/assets/videos/product-video.mp4" type="video/mp4" />
                  Tarayıcınız video etiketini desteklemiyor.
                </video>
              </div>
            </div>
          </div>
        </div>
      )}
      {ratingModalOpen && product?.soldListingSummary && (
        <RatingModal
          soldListingId={product.soldListingSummary.id}
          listingTitle={product.title}
          onSubmitted={() =>
            setProduct((prev) =>
              prev && prev.soldListingSummary
                ? {
                    ...prev,
                    soldListingSummary: { ...prev.soldListingSummary, hasRating: true },
                  }
                : prev,
            )
          }
          onClose={() => setRatingModalOpen(false)}
        />
      )}
      {product && (
        <ShareListingModal
          show={showShareModal}
          onClose={() => setShowShareModal(false)}
          listingId={product.id}
          listingTitle={product.title}
          triggerRef={shareButtonRef}
        />
      )}
    </div>
  );
};

export default Ilan_detay_login;